/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {memo} from "react";
import humanizeString from "string-humanize";
import {get, isBoolean} from "lodash";
import {Badge, Card} from "reactstrap";
import {FaArrowDown, FaArrowUp, FaTrash} from "react-icons/fa";
import Toggle from "react-toggle";
import PropTypes from "prop-types";
import Popover from "../Popover";
import PopoverItem from "../Popover/PopoverItem";
import ProtectedContent from "../ProtectedContent";
import Tooltip from "../Tooltip/Tooltip";
import "./tableStyles.scss";

const buildReadableHeader = (header) => {
  let header_list = header.split(".");
  header = header_list[header_list.length - 1];
  return humanizeString(header).replace("Is ", "");
};

const Table = (props) => {
  const {
    actionsList = [],
    booleanAsToggle = false,
    customRenderKeys = [],
    customRenderComponent: CustomRenderComponent,
    data = [],
    filterActionsOn,
    handleSort = () => {},
    headers = [],
    onClickToggle = () => {},
    onSelectRow = () => {},
    onSelectAction = () => {},
    permsForActions = [],
    rowClickable = false,
    sortColumn,
    sortDirection,
    tableName = "table",
    toggleTestId = "toggle",
    withActions = false,
    sortableFields = [],
  } = props;

  // The render* helpers below are plain functions (called, not used as <Components />),
  // so React doesn't treat them as new component types on every render.

  const renderTableHeader = () => {
    return (
      <tr className="table-left">
        {headers.map((header) => {
          const isSortable = sortableFields.some((field) => field.value === header);
          return (
            <th
              key={header}
              onClickCapture={isSortable ? () => handleSort(header) : undefined}
              className={`roboto-bold table__header ${tableName}--${header} ${
                isSortable ? "cursor-pointer" : ""
              }`}
              data-title={header}
            >
              <span>{buildReadableHeader(header)}</span>
              {isSortable && sortColumn === header && (
                <span style={{marginLeft: "8px"}}>
                  {sortDirection === "asc" ? <FaArrowUp /> : <FaArrowDown />}
                </span>
              )}
            </th>
          );
        })}
        {withActions && (
          <ProtectedContent perms={permsForActions} shouldHaveAllPermissions={false}>
            <th className="roboto-bold table__header table-center">
              <span>Actions</span>
            </th>
          </ProtectedContent>
        )}
      </tr>
    );
  };

  const renderBoolean = (value, rowData, renderKey) => {
    if (booleanAsToggle) {
      return (
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Toggle
            checked={value}
            onChange={() => onClickToggle(rowData, renderKey, value)}
            data-cy={toggleTestId}
          />
        </div>
      );
    } else {
      return (
        <Badge color="" className="badge-dot ml-1">
          {value ? <i className="bg-success" /> : <i className="bg-warning" />}
        </Badge>
      );
    }
  };

  const getActions = (row) => {
    if (filterActionsOn) {
      if (row[filterActionsOn]) {
        return actionsList;
      } else {
        return [{label: "Delete", icon: FaTrash}];
      }
    } else {
      return actionsList;
    }
  };

  const renderTableData = (rowData, renderKey) => {
    const value = get(rowData, renderKey);
    if (customRenderKeys?.includes(renderKey)) {
      return <CustomRenderComponent data={rowData} renderKey={renderKey} />;
    } else if (isBoolean(value)) {
      return renderBoolean(value, rowData, renderKey);
    } else if (Array.isArray(value)) {
      return value.join(", ");
    } else {
      return value ?? "-";
    }
  };

  const renderTableBody = () => {
    return data.map((eachRow, rowIndex) => {
      return (
        <tr
          key={`row-${eachRow.id}`}
          className={rowIndex % 2 === 0 ? "table__row--active table-left" : "table-left"}
          data-cy={`${tableName}-row-${rowIndex}`}
        >
          {headers.map((header, i) => (
            <React.Fragment key={`row-${eachRow.id}-column-${i}`}>
              <td
                onClick={
                  rowClickable
                    ? () => {
                        onSelectRow(eachRow);
                      }
                    : null
                }
                className={`roboto-normal ${rowClickable ? "cursor-pointer" : ""}`}
                id={`${tableName}-${header}-${rowIndex}${i}`}
                data-cy={`${tableName}-${header}`}
              >
                {renderTableData(eachRow, header)}
              </td>
              <Tooltip
                key={String(get(eachRow, header))}
                target={`${tableName}-${header}-${rowIndex}${i}`}
              />
            </React.Fragment>
          ))}

          {withActions && (
            <ProtectedContent perms={permsForActions} shouldHaveAllPermissions={false}>
              <td className="table-center">
                <Popover>
                  {getActions(eachRow).map((action, index) => {
                    const {
                      label,
                      icon,
                      perms,
                      condition = "",
                      conditionValue = true,
                    } = action;
                    if (condition && eachRow[condition] !== conditionValue) {
                      return null;
                    }
                    return (
                      <PopoverItem
                        key={index}
                        onContentSelection={(action) => onSelectAction(action, eachRow)}
                        label={label}
                        icon={icon}
                        perms={perms}
                      />
                    );
                  })}
                </Popover>
              </td>
            </ProtectedContent>
          )}
        </tr>
      );
    });
  };

  return (
    <Card className="shadow">
      <table className="table zeus-table table-curved">
        <thead>{renderTableHeader()}</thead>
        <tbody>{renderTableBody()}</tbody>
      </table>
    </Card>
  );
};

Table.propTypes = {
  /** List of headers to display on table.
   *  They need to match the row object keys */
  headers: PropTypes.arrayOf(PropTypes.string).isRequired,
  /** Data which is need to be displayed */
  data: PropTypes.array.isRequired,
  /** Name for the table which will used in giving id to table data */
  tableName: PropTypes.string,
  /** This prop will determine wether row is clickable */
  rowClickable: PropTypes.bool,
  /** Callback function which will be executed when clicked on row */
  onSelectRow: PropTypes.func,
  /** Wether to display action column on table */
  withActions: PropTypes.bool,
  /** List of actions which will be passed to action popover */
  actionsList: PropTypes.array,
  /** Permissions needed for actions */
  permsForActions: PropTypes.array,
  /** Function which will be executed when clicked on action */
  onSelectAction: PropTypes.func,
  /** If this prop is set to true boolean values will be displayed as toggle */
  booleanAsToggle: PropTypes.bool,
  /** id which will be passed as data-cy attribute to toggle component */
  toggleTestId: PropTypes.string,
  /** Callback function which will be executed when clicked on toggle */
  onClickToggle: PropTypes.func,
  /** Key for which custom cell will be rendered.
   *  This key should be one of headers
   */
  customRenderKeys: PropTypes.arrayOf(PropTypes.string),
  /** Function which will be executed for custom cell
   *  This should return react element
   */
  customRenderComponent: PropTypes.func,
  /** Columns on the basis of whose values rows will  be sorted */
  sortableFields: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      type: PropTypes.oneOf(["date"]),
    })
  ),
  /** Callback function which will be executed when clicked on a sortable header */
  handleSort: PropTypes.func,
  /** Column on which data is currently sorted, used to render the sort arrow */
  sortColumn: PropTypes.string,
  /** Direction in which data is currently sorted, used to render the sort arrow */
  sortDirection: PropTypes.string,
};

export default memo(Table);
